import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "../components/site/SiteLayout";
import bannerOne from "../assets/banners/banner-1.svg";
import bannerTwo from "../assets/banners/banner-2.svg";
import bannerThree from "../assets/banners/banner-3.svg";
import initiativeEducation from "../assets/initiatives/education.svg";
import initiativeNutrition from "../assets/initiatives/nutrition.svg";
import initiativeWomen from "../assets/initiatives/women.svg";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000").replace(
  /\/+$/,
  ""
);

const fallbackSlides = [
  {
    title: "Empowering Lives, Creating Opportunities",
    description:
      "We partner with communities to deliver education, health, and livelihood programs that last.",
    image: bannerOne,
    stat: "42,000+ lives supported"
  },
  {
    title: "Stronger Communities Start Here",
    description:
      "From classrooms to clinics, we build safe spaces where families can thrive together.",
    image: bannerTwo,
    stat: "210 villages reached"
  },
  {
    title: "Every Campaign Tells a Story",
    description:
      "Join volunteers and donors who are funding the next generation of change makers.",
    image: bannerThree,
    stat: "1,100 volunteers active"
  }
];

const fallbackInitiatives = [
  {
    title: "Education for Every Child",
    description: "After-school programs, digital labs, and scholarships for rural learners.",
    image: initiativeEducation
  },
  {
    title: "Child Nutrition & Care",
    description: "Daily meal plans, health checkups, and maternal support for growing families.",
    image: initiativeNutrition
  },
  {
    title: "Women Empowerment",
    description: "Skill training, micro-entrepreneurship, and leadership circles for women.",
    image: initiativeWomen
  }
];

const fallbackStats = [
  { label: "Students educated", value: "10,800+" },
  { label: "Healthcare beneficiaries", value: "5,450+" },
  { label: "Projects completed", value: "27" }
];

const fallbackVisionMission = {
  missionTitle: "Our Mission",
  missionDescription:
    "To ensure every child and family has access to education, health services, and sustainable income opportunities.",
  visionTitle: "Our Vision",
  visionDescription:
    "To build resilient communities where dignity, safety, and opportunity are within reach for all."
};

const stories = [
  {
    name: "Sana Khan",
    role: "Community Teacher",
    quote:
      "The new learning hub helped us bring 200 children back to school with confidence and hope."
  },
  {
    name: "Rahul Das",
    role: "Health Volunteer",
    quote:
      "Mobile clinics now reach families who had no access to doctors before this program."
  },
  {
    name: "Asha Devi",
    role: "Women Leader",
    quote:
      "Skill training empowered 60 women in my village to start home businesses this year."
  }
];

const news = [
  {
    title: "Monsoon Relief Kits Delivered",
    date: "May 2, 2026",
    summary: "2,500 relief kits distributed across three flood-affected districts."
  },
  {
    title: "New STEM Lab Launched",
    date: "April 18, 2026",
    summary: "A solar-powered learning lab now serves 600 students each week."
  },
  {
    title: "Volunteer Summit 2026",
    date: "March 29, 2026",
    summary: "600 volunteers trained on youth mentorship, safety, and impact reporting."
  }
];

const partners = [
  "BrightAid",
  "CareWorks",
  "Sustain Trust",
  "Global Relief",
  "Unity Bank",
  "Teach Forward"
];

function Home() {
  const [slides, setSlides] = useState(fallbackSlides);
  const [stats, setStats] = useState(fallbackStats);
  const [initiatives, setInitiatives] = useState(fallbackInitiatives);
  const [visionMission, setVisionMission] = useState(fallbackVisionMission);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (slides.length < 2) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 7000);

    return () => clearInterval(intervalId);
  }, [slides.length]);

  useEffect(() => {
    const fetchJson = async (url) => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to load homepage data");
      }
      return response.json();
    };

    const loadHomepage = async () => {
      try {
        const [bannerData, visionData, statsData, initiativeData] = await Promise.all([
          fetchJson(`${API_BASE_URL}/cms/public/banners/`),
          fetchJson(`${API_BASE_URL}/cms/public/vision-mission/`),
          fetchJson(`${API_BASE_URL}/cms/public/statistics/`),
          fetchJson(`${API_BASE_URL}/cms/public/initiatives/`)
        ]);

        if (Array.isArray(bannerData.items) && bannerData.items.length > 0) {
          const mappedSlides = bannerData.items.map((item, index) => ({
            title: item.title,
            description: item.description,
            image: item.image_url,
            stat: fallbackSlides[index % fallbackSlides.length]?.stat || "Impact since 2012"
          }));
          setSlides(mappedSlides);
          setActiveIndex(0);
        }

        if (visionData.item) {
          setVisionMission({
            missionTitle: visionData.item.mission_title,
            missionDescription: visionData.item.mission_description,
            visionTitle: visionData.item.vision_title,
            visionDescription: visionData.item.vision_description
          });
        }

        if (Array.isArray(statsData.items) && statsData.items.length > 0) {
          setStats(statsData.items.map((item) => ({ label: item.label, value: item.value })));
        }

        if (Array.isArray(initiativeData.items) && initiativeData.items.length > 0) {
          setInitiatives(
            initiativeData.items.map((item, index) => ({
              title: item.title,
              description: item.description,
              image: item.image_url || fallbackInitiatives[index % fallbackInitiatives.length].image
            }))
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadHomepage();
  }, []);

  const activeSlide = slides[activeIndex];

  return (
    <SiteLayout>
      <section className="container hero">
        <div>
          <div className="hero-kicker">Hope Foundation NGO</div>
          <h1 className="hero-title">{activeSlide.title}</h1>
          <p className="hero-text">{activeSlide.description}</p>
          <div className="hero-actions">
            <Link to="/donate" className="btn btn-secondary">
              Donate Now
            </Link>
            <Link to="/get-involved" className="btn btn-primary">
              Get Involved
            </Link>
            <Link to="/projects" className="btn btn-ghost">
              Explore Projects
            </Link>
          </div>
          <div className="slider-dots">
            {slides.map((slide, index) => (
              <button
                key={slide.title}
                className={`slider-dot ${index === activeIndex ? "active" : ""}`}
                onClick={() => setActiveIndex(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
        <div className="hero-media">
          <img src={activeSlide.image} alt={activeSlide.title} />
          <div className="hero-badge">
            <strong>{activeSlide.stat}</strong>
            Impact since 2012
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Mission & Vision</h2>
          <p className="section-subtitle">
            We deliver long-term change by combining compassionate care with measurable community
            programs.
          </p>
          <div className="card-grid">
            <div className="card" style={{ "--i": 0 }}>
              <h3>{visionMission.missionTitle}</h3>
              <p>{visionMission.missionDescription}</p>
            </div>
            <div className="card" style={{ "--i": 1 }}>
              <h3>{visionMission.visionTitle}</h3>
              <p>{visionMission.visionDescription}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Impact at a Glance</h2>
          <p className="section-subtitle">
            Transparent reporting keeps every donor and volunteer connected to real outcomes.
          </p>
          <div className="card-grid">
            {stats.map((stat, index) => (
              <div className="card stat-card" key={stat.label} style={{ "--i": index }}>
                <div className="stat-value">{stat.value}</div>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Our Initiatives</h2>
          <p className="section-subtitle">
            Community-designed programs that meet people where they are and grow with them.
          </p>
          <div className="card-grid">
            {initiatives.map((initiative, index) => (
              <div className="card initiative-card" key={initiative.title} style={{ "--i": index }}>
                <img src={initiative.image} alt={initiative.title} />
                <h3>{initiative.title}</h3>
                <p>{initiative.description}</p>
                <Link to="/projects" className="btn btn-ghost">
                  See Details
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Success Stories</h2>
          <p className="section-subtitle">
            Stories from the people and partners who create impact every day.
          </p>
          <div className="card-grid">
            {stories.map((story, index) => (
              <div className="card quote-card" key={story.name} style={{ "--i": index }}>
                <p>"{story.quote}"</p>
                <strong>{story.name}</strong>
                <div className="news-meta">{story.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="cta-strip">
            <div>
              <h2>Ready to stand with us?</h2>
              <p>
                Join monthly donors and volunteer teams bringing education, safety, and hope to
                thousands of families.
              </p>
            </div>
            <div className="hero-actions">
              <Link to="/donate" className="btn btn-secondary">
                Donate Now
              </Link>
              <Link to="/get-involved" className="btn btn-ghost">
                Volunteer With Us
              </Link>
              <Link to="/campaign" className="btn btn-ghost">
                Join Events
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Latest News & Events</h2>
          <p className="section-subtitle">
            Campaign updates, field reports, and partner highlights from across our regions.
          </p>
          <div className="card-grid">
            {news.map((item, index) => (
              <div className="card" key={item.title} style={{ "--i": index }}>
                <div className="news-meta">{item.date}</div>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
                <Link to="/blog" className="btn btn-ghost">
                  Read Update
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Partners & Supporters</h2>
          <p className="section-subtitle">
            We collaborate with civic leaders, foundations, and mission-aligned companies.
          </p>
          <div className="partner-row">
            {partners.map((partner, index) => (
              <div className="partner-badge" key={partner} style={{ "--i": index }}>
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export default Home;
